import React, { ReactElement, useEffect, useState } from 'react'
import { observer } from 'mobx-react'

import { PrimaryButton } from '../../components/Button'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import Modal, { ModalFooter, ModalProps } from '../../components/Modal'
import Radom, { Address, CreateExternalAccountRequest, ExternalAccountOwnerType, ExternalAccountType } from '../../state/Radom'
import {
  AccountKind,
  AccountKindTiles,
  FieldGroup,
  FieldHint,
  FormStack,
  LockedField,
  ModalSection,
  RailOption,
  ResponsiveFields,
  SectionDescription,
  SectionHeading,
  SectionTitle
} from '../../components/AccountDrawerParts'
import { NetworkIcon, NetworkRow } from '../../components/AssetIdentity'
import { methodNetworkLabel } from '../../util/AssetLabels'
import { ManagedPaymentMethod, getPaymentMethods } from '../../util/Managed'
import { errorToast, successToast, useTestMode } from '../../util/Util'
import { AddressForm } from '../Verify/AddressForm'
import { Countries } from '../Verify/Countries'

/** The contact this account belongs to; used to prefill the holder details. */
export interface AccountContact {
  name?: string | null
  email?: string | null
  accountType?: string | null
  companyName?: string | null
}

type Props = ModalProps & {
  customerId: string
  contact?: AccountContact | null
  onAccountCreated: () => void
  preselectedMethod?: ManagedPaymentMethod
  forcedCategory?: PayoutAccountCategory
  /** A pasted wallet address the account is being created for. */
  initialCryptoAddress?: string
  /** A pasted IBAN the account is being created for. */
  initialIban?: string
  /** Networks a pasted address can live on; the picker offers only these. */
  allowedNetworks?: string[]
}

export enum PayoutAccountCategory {
  Crypto = 'Crypto',
  Fiat = 'Fiat',
}

enum FiatAccountTypeOption {
  US = 'USD account',
  IBAN = 'EUR account',
}

enum AccountOwnerTypeOption {
  Individual = 'Individual',
  Business = 'Business',
}

const formatAccountError = (raw: string): string => {
  const fieldMap: Record<string, string> = {
    'payee.iban.iban': 'IBAN',
    'payee.iban.bic': 'BIC',
    'payee.account.account_number': 'Account number',
    'payee.account.routing_number': 'Routing number'
  }

  const fieldMatch = raw.match(/\(field:\s*([^)]+)\)/)
  if (fieldMatch) {
    const fieldName = fieldMap[fieldMatch[1].trim()] ?? fieldMatch[1].trim()
    const message = raw.replace(/\s*\(field:.*\)/, '').trim()
    return `${message} — please check your ${fieldName}`
  }

  return raw
}

const CreateExternalAccountModal = observer((
  { customerId, contact, onAccountCreated, preselectedMethod, forcedCategory, initialCryptoAddress, initialIban, allowedNetworks, ...props }: Props
): ReactElement => {
  const [testMode] = useTestMode()
  const [category, setCategory] = useState<PayoutAccountCategory>(
    forcedCategory ?? PayoutAccountCategory.Crypto
  )

  // Crypto fields
  const [selectedCryptoType, setSelectedCryptoType] = useState<ManagedPaymentMethod | undefined>(preselectedMethod)
  const [cryptoAddress, setCryptoAddress] = useState(initialCryptoAddress ?? '')
  const [cryptoAccountName, setCryptoAccountName] = useState('')

  // Common fiat fields. The holder's name is asked for once: first and last
  // name for a person, the registered name for a business.
  const [accountOwnerType, setAccountOwnerType] = useState<AccountOwnerTypeOption>()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const accountOwnerName = accountOwnerType === AccountOwnerTypeOption.Business
    ? businessName.trim()
    : `${firstName.trim()} ${lastName.trim()}`.trim()
  const [bankName, setBankName] = useState('')
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountTypeOption | undefined>(
    initialIban ? FiatAccountTypeOption.IBAN : undefined
  )
  const [accountOwnerAddress, setAccountOwnerAddress] = useState<Address>({} as any)

  // US fields
  const [usAccountNumber, setUsAccountNumber] = useState('')
  const [usRoutingNumber, setUsRoutingNumber] = useState('')
  const [usCheckingOrSavings, setUsCheckingOrSavings] = useState<'checking' | 'savings'>('checking')

  // IBAN fields
  const [ibanAccountNumber, setIbanAccountNumber] = useState(initialIban ?? '')
  const [ibanBic, setIbanBic] = useState('')
  const [ibanCountry, setIbanCountry] = useState(initialIban ? initialIban.slice(0, 2).toUpperCase() : '')

  const [isLoading, setIsLoading] = useState(false)

  // A paste from the Send page (an address or an IBAN) survives the reset:
  // the drawer opened to save exactly that.
  const resetForm = (): void => {
    setCategory(forcedCategory ?? PayoutAccountCategory.Crypto)
    setSelectedCryptoType(preselectedMethod)
    setCryptoAddress(initialCryptoAddress ?? '')
    setCryptoAccountName('')
    setAccountOwnerType(undefined)
    setFirstName('')
    setLastName('')
    setBusinessName('')
    setBankName('')
    setFiatAccountType(initialIban ? FiatAccountTypeOption.IBAN : undefined)
    setAccountOwnerAddress({} as any)
    setUsAccountNumber('')
    setUsRoutingNumber('')
    setUsCheckingOrSavings('checking')
    setIbanAccountNumber(initialIban ?? '')
    setIbanBic('')
    setIbanCountry(initialIban ? initialIban.slice(0, 2).toUpperCase() : '')
  }

  // Start from what we already know about the contact each time the drawer
  // opens, so the holder details only need checking, not typing.
  useEffect(() => {
    if (!props.visible) return
    resetForm()
    const name = contact?.name?.trim() ?? ''
    const companyName = contact?.companyName?.trim() ?? ''
    const type = contact?.accountType?.toLowerCase()
    const [first, ...rest] = name.split(/\s+/).filter(Boolean)
    setCryptoAccountName(name)
    setFirstName(rest.length > 0 ? first : name)
    setLastName(rest.join(' '))
    setBusinessName(companyName || name)
    setAccountOwnerType(type === 'business' || companyName
      ? AccountOwnerTypeOption.Business
      : AccountOwnerTypeOption.Individual)
  }, [props.visible, customerId])

  const cryptoMethods = getPaymentMethods()
    .filter(m => !m.isFiat && !m.isDisabled && !m.isHidden && !m.hostChain)
    .filter(m => testMode ? m.isTestnet : !m.isTestnet)
    .filter(m => !allowedNetworks || allowedNetworks.includes(m.name))

  // ---- Fiat helpers ----

  const getFiatAccountType = (): ExternalAccountType => {
    switch (fiatAccountType) {
    case FiatAccountTypeOption.US: return 'us'
    case FiatAccountTypeOption.IBAN: return 'iban'
    default: return 'us'
    }
  }

  const getOwnerType = (): ExternalAccountOwnerType | undefined => {
    switch (accountOwnerType) {
    case AccountOwnerTypeOption.Individual: return 'individual'
    case AccountOwnerTypeOption.Business: return 'business'
    default: return undefined
    }
  }

  const getCurrency = (): string => {
    switch (fiatAccountType) {
    case FiatAccountTypeOption.US: return 'usd'
    case FiatAccountTypeOption.IBAN: return 'eur'
    default: return 'usd'
    }
  }

  // ---- Validation ----

  const isCryptoFormValid = (): boolean => {
    return !!selectedCryptoType && !!cryptoAddress.trim() && !!cryptoAccountName.trim()
  }

  const isFiatFormValid = (): boolean => {
    if (!fiatAccountType || !accountOwnerType || !accountOwnerName) return false
    if (!accountOwnerAddress.country || !accountOwnerAddress.city || !accountOwnerAddress.streetLine1) return false

    if (accountOwnerAddress.country === 'USA' && !accountOwnerAddress.subdivision) return false
    if (Countries.find(s => s.alpha3 === accountOwnerAddress.country)?.postal_code_format && !accountOwnerAddress.postalCode) return false

    if (fiatAccountType === FiatAccountTypeOption.US && (!usAccountNumber || !usRoutingNumber)) return false
    if (fiatAccountType === FiatAccountTypeOption.IBAN && (!ibanAccountNumber || !ibanBic)) return false

    return true
  }

  const isFormValid = (): boolean => {
    return category === PayoutAccountCategory.Crypto ? isCryptoFormValid() : isFiatFormValid()
  }

  // ---- Submit ----

  const createAccount = async (): Promise<void> => {
    if (!isFormValid()) return

    setIsLoading(true)
    try {
      if (category === PayoutAccountCategory.Crypto) {
        const req: CreateExternalAccountRequest = {
          customer_id: customerId,
          currency: selectedCryptoType!.ticker.toLowerCase(),
          account_type: 'crypto',
          account_owner_name: cryptoAccountName.trim(),
          crypto: {
            address: cryptoAddress.trim(),
            network: selectedCryptoType!.name
          }
        }
        await Radom.createExternalAccount(req)
      } else {
        const req: CreateExternalAccountRequest = {
          customer_id: customerId,
          currency: getCurrency(),
          account_type: getFiatAccountType(),
          account_owner_name: accountOwnerName,
          account_owner_type: getOwnerType(),
          bank_name: bankName.trim() || undefined,
          first_name: accountOwnerType === AccountOwnerTypeOption.Individual ? firstName.trim() : undefined,
          last_name: accountOwnerType === AccountOwnerTypeOption.Individual ? lastName.trim() : undefined,
          business_name: accountOwnerType === AccountOwnerTypeOption.Business ? businessName.trim() : undefined,
          address: {
            street_line_1: accountOwnerAddress.streetLine1,
            street_line_2: accountOwnerAddress.streetLine2 || undefined,
            city: accountOwnerAddress.city,
            state: accountOwnerAddress.subdivision || undefined,
            postal_code: accountOwnerAddress.postalCode || undefined,
            country: accountOwnerAddress.country
          }
        }

        if (fiatAccountType === FiatAccountTypeOption.US) {
          req.account = {
            account_number: usAccountNumber,
            routing_number: usRoutingNumber,
            checking_or_savings: usCheckingOrSavings
          }
        } else if (fiatAccountType === FiatAccountTypeOption.IBAN) {
          req.iban = {
            account_number: ibanAccountNumber,
            bic: ibanBic || undefined,
            country: ibanCountry || accountOwnerAddress.country
          }
        }

        await Radom.createExternalAccount(req)
      }

      resetForm()
      onAccountCreated()
      successToast('Account saved')
    } catch (err: any) {
      const raw: string = err.response?.data?.error || err.message || 'Unknown error'
      errorToast(formatAccountError(raw))
    }
    setIsLoading(false)
  }

  // ---- Render ----

  const contactName = contact?.name?.trim() || ''
  const holderLabel = contactName || contact?.email?.trim() || 'this contact'
  const selectPlaceholder = { fontSize: 14, color: 'var(--icon-muted, #747894)' }
  const fiatOptions: Array<{ value: FiatAccountTypeOption, label: string, hint: string, flags: string[] }> = [
    { value: FiatAccountTypeOption.IBAN, label: 'EUR account', hint: 'Euro transfers by IBAN', flags: ['EU', 'GB'] },
    { value: FiatAccountTypeOption.US, label: 'USD account', hint: 'US dollar transfers by ACH or wire', flags: ['US'] }
  ]
  const selectedRail = fiatOptions.find(option => option.value === fiatAccountType)

  const renderCryptoForm = (): ReactElement => (
    <ModalSection>
      <SectionHeading>
        <SectionTitle>Wallet</SectionTitle>
        <SectionDescription>Where {holderLabel} receives crypto.</SectionDescription>
      </SectionHeading>

      <FormStack>
        {contactName
          ? <LockedField
            label='Held by'
            value={contactName}
            hint={`Radom records ${contactName} as the beneficiary of every transfer to this wallet.`} />
          : <TextInputWithLabel
            label='Held by'
            placeholder="The wallet holder's name"
            disabled={isLoading}
            required
            value={cryptoAccountName}
            onChange={e => setCryptoAccountName((e.target as HTMLInputElement).value)}
            onBlur={() => setCryptoAccountName(prev => prev.trim())}
          />}

        <FieldGroup>
          <InputLabel>Network</InputLabel>
          <Dropdown
            key={selectedCryptoType?.name}
            disabled={isLoading}
            outerStyle={{ minHeight: 46 }}
            selectedContent={
              selectedCryptoType
                ? <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                  <NetworkIcon method={selectedCryptoType} size={24} />
                  <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>
                    {methodNetworkLabel(selectedCryptoType)}
                  </span>
                </div>
                : <span style={selectPlaceholder}>Select network</span>
            }
            dropdownContent={
              cryptoMethods.map(m =>
                <DropdownItem
                  key={m.name}
                  onClick={() => setSelectedCryptoType(m)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                  <NetworkRow method={m} size={24} />
                </DropdownItem>
              )
            } />
        </FieldGroup>

        <TextInputWithLabel
          label='Wallet address'
          placeholder='Paste the wallet address'
          disabled={isLoading}
          required
          value={cryptoAddress}
          onChange={e => setCryptoAddress((e.target as HTMLInputElement).value)}
          onBlur={() => setCryptoAddress(prev => prev.trim())}
        />
      </FormStack>
    </ModalSection>
  )

  const renderFiatForm = (): ReactElement => (
    <FormStack>
      <ModalSection>
        <SectionHeading>
          <SectionTitle>Account holder</SectionTitle>
          <SectionDescription>Who this bank account belongs to.</SectionDescription>
        </SectionHeading>

        <FormStack>
          <FieldGroup>
            <InputLabel>Held by</InputLabel>
            <Dropdown
              key={accountOwnerType}
              disabled={isLoading}
              outerStyle={{ minHeight: 46 }}
              selectedContent={
                accountOwnerType
                  ? <span style={{ fontSize: 14 }}>{accountOwnerType}</span>
                  : <span style={selectPlaceholder}>Business or individual</span>
              }
              dropdownContent={
                [AccountOwnerTypeOption.Individual, AccountOwnerTypeOption.Business].map(c =>
                  <DropdownItem key={c} onClick={() => setAccountOwnerType(c)} style={{ fontSize: 14 }}>
                    {c}
                  </DropdownItem>
                )
              } />
          </FieldGroup>

          {accountOwnerType === AccountOwnerTypeOption.Individual && <ResponsiveFields>
            <TextInputWithLabel
              label='First name'
              placeholder='First name'
              disabled={isLoading}
              required
              value={firstName}
              onChange={e => setFirstName((e.target as HTMLInputElement).value)}
              onBlur={() => setFirstName(prev => prev.trim())}
            />
            <TextInputWithLabel
              label='Last name'
              placeholder='Last name'
              disabled={isLoading}
              required
              value={lastName}
              onChange={e => setLastName((e.target as HTMLInputElement).value)}
              onBlur={() => setLastName(prev => prev.trim())}
            />
          </ResponsiveFields>}

          {accountOwnerType === AccountOwnerTypeOption.Business && <TextInputWithLabel
            label='Business name'
            placeholder='Registered business name'
            disabled={isLoading}
            required
            value={businessName}
            onChange={e => setBusinessName((e.target as HTMLInputElement).value)}
            onBlur={() => setBusinessName(prev => prev.trim())}
          />}

          {accountOwnerType && <FieldHint>
            Must match the name on the bank account. Banks check the payee name before accepting a transfer.
          </FieldHint>}
        </FormStack>
      </ModalSection>

      <ModalSection>
        <SectionHeading>
          <SectionTitle>Bank details</SectionTitle>
          <SectionDescription>Where the money is sent.</SectionDescription>
        </SectionHeading>

        <FormStack>
          <FieldGroup>
            <InputLabel>Account format</InputLabel>
            <Dropdown
              key={fiatAccountType}
              disabled={isLoading}
              outerStyle={{ minHeight: 46 }}
              selectedContent={
                selectedRail
                  ? <RailOption label={selectedRail.label} hint={selectedRail.hint} flags={selectedRail.flags} />
                  : <span style={selectPlaceholder}>EUR or USD</span>
              }
              dropdownContent={
                fiatOptions.map(option =>
                  <DropdownItem
                    key={option.value}
                    onClick={() => setFiatAccountType(option.value)}
                    style={{ fontSize: 14, alignItems: 'center', display: 'flex', gap: 10, padding: '11px 12px' }}>
                    <RailOption label={option.label} hint={option.hint} flags={option.flags} />
                  </DropdownItem>
                )
              } />
          </FieldGroup>

          {fiatAccountType === FiatAccountTypeOption.IBAN && <ResponsiveFields>
            <TextInputWithLabel
              label='IBAN'
              placeholder='Enter IBAN'
              disabled={isLoading}
              required
              value={ibanAccountNumber}
              onChange={e => setIbanAccountNumber((e.target as HTMLInputElement).value)}
              onBlur={() => setIbanAccountNumber(prev => prev.replace(/\s+/g, '').toUpperCase())}
            />
            <TextInputWithLabel
              label='BIC'
              placeholder='Enter BIC'
              disabled={isLoading}
              required
              value={ibanBic}
              onChange={e => setIbanBic((e.target as HTMLInputElement).value)}
              onBlur={() => setIbanBic(prev => prev.replace(/\s+/g, '').toUpperCase())}
            />
          </ResponsiveFields>}

          {fiatAccountType === FiatAccountTypeOption.US && <>
            <ResponsiveFields>
              <TextInputWithLabel
                label='Account number'
                placeholder='Enter account number'
                disabled={isLoading}
                required
                value={usAccountNumber}
                onChange={e => setUsAccountNumber((e.target as HTMLInputElement).value)}
                onBlur={() => setUsAccountNumber(prev => prev.replace(/\D+/g, ''))}
              />
              <TextInputWithLabel
                label='Routing number'
                placeholder='9 digits'
                disabled={isLoading}
                required
                value={usRoutingNumber}
                onChange={e => setUsRoutingNumber((e.target as HTMLInputElement).value)}
                onBlur={() => setUsRoutingNumber(prev => prev.replace(/\D+/g, '').slice(0, 9))}
              />
            </ResponsiveFields>
            <FieldGroup>
              <InputLabel>Checking or savings</InputLabel>
              <Dropdown
                disabled={isLoading}
                outerStyle={{ minHeight: 46 }}
                selectedContent={
                  <span style={{ fontSize: 14 }}>{usCheckingOrSavings === 'checking' ? 'Checking' : 'Savings'}</span>}
                dropdownContent={
                  (['checking', 'savings'] as const).map(c =>
                    <DropdownItem key={c} onClick={() => setUsCheckingOrSavings(c)} style={{ fontSize: 14 }}>
                      {c === 'checking' ? 'Checking' : 'Savings'}
                    </DropdownItem>
                  )
                } />
            </FieldGroup>
          </>}

          {fiatAccountType && <TextInputWithLabel
            label='Bank name'
            placeholder='e.g. Chase, Wells Fargo'
            disabled={isLoading}
            value={bankName}
            onChange={e => setBankName((e.target as HTMLInputElement).value)}
            onBlur={() => setBankName(prev => prev.trim())}
          />}
        </FormStack>
      </ModalSection>

      {fiatAccountType && <ModalSection>
        <SectionHeading>
          <SectionTitle>Account holder address</SectionTitle>
          <SectionDescription>The address the bank has on file for {holderLabel}.</SectionDescription>
        </SectionHeading>
        <AddressForm state={accountOwnerAddress} onUpdate={(s) => setAccountOwnerAddress(s)} />
      </ModalSection>}
    </FormStack>
  )

  const kind: AccountKind = category === PayoutAccountCategory.Crypto ? 'wallet' : 'bank'

  return <Modal {...props} innerStyles={props.presentation === 'drawer' ? undefined : { width: 550 }}>
    <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 16 }}>
      {!preselectedMethod && !forcedCategory && <ModalSection>
        <SectionHeading>
          <SectionTitle>Account type</SectionTitle>
          <SectionDescription>
            A wallet on a network, or a bank account in {holderLabel}&apos;s name.
          </SectionDescription>
        </SectionHeading>
        <AccountKindTiles
          value={kind}
          onChange={next => setCategory(next === 'wallet' ? PayoutAccountCategory.Crypto : PayoutAccountCategory.Fiat)}
          disabled={isLoading}
          stacked={props.presentation === 'drawer'}
          testMode={testMode} />
      </ModalSection>}

      {category === PayoutAccountCategory.Crypto ? renderCryptoForm() : renderFiatForm()}
    </div>

    <ModalFooter $fullWidthAction>
      <PrimaryButton
        disabled={!isFormValid()}
        isLoading={isLoading}
        style={{ width: '100%' }}
        onClick={createAccount}>
        {kind === 'wallet' ? 'Save wallet' : 'Save bank account'}
      </PrimaryButton>
    </ModalFooter>
  </Modal>
})

export default CreateExternalAccountModal
